import ObjectHash from 'object-hash'

export const state = () => {
	return {
		statsGlobal: {},
		ttl_statsGlobal: {},
		cache_key_statsGlobal: {},

		statsRewards: {},
		ttl_statsRewards: {},
		cache_key_statsRewards: {},

		statsPoints: {},
		ttl_statsPoints: {},
		cache_key_statsPoints: {},

		statsVip: {},
		ttl_statsVip: {},
		cache_key_statsVip: {},

		cache_duration: 10800000, // 3 hours
	}
}

function isInCacheStatsGlobal(state, statKey, companyId, params) {
	if (!state.ttl_statsGlobal[statKey] || state.ttl_statsGlobal[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
	return state.cache_key_statsGlobal[statKey] === cacheKey
}

function isInCacheStatsRewards(state, statKey, companyId, params) {
	if (!state.ttl_statsRewards[statKey] || state.ttl_statsRewards[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsRewards(statKey, companyId, params)
	return state.cache_key_statsRewards[statKey] === cacheKey
}

function buildCacheKeyStatsGlobal(statKey, companyId, params) {
	return `company:${companyId}:global::params:${ObjectHash(params)}:${statKey}`
}

function buildCacheKeyStatsRewards(statKey, companyId, params) {
	return `company:${companyId}:rewards::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsVip(state, statKey, companyId, params) {
	if (!state.ttl_statsVip[statKey] || state.ttl_statsVip[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsVip(statKey, companyId, params)
	return state.cache_key_statsVip[statKey] === cacheKey
}

function buildCacheKeyStatsVip(statKey, companyId, params) {
	return `company:${companyId}:vip::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsPoints(state, statKey, companyId, params) {
	if (!state.ttl_statsPoints[statKey] || state.ttl_statsPoints[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsPoints(statKey, companyId, params)
	return state.cache_key_statsPoints[statKey] === cacheKey
}

function buildCacheKeyStatsPoints(statKey, companyId, params) {
	return `company:${companyId}:points::params:${ObjectHash(params)}:${statKey}`
}

export const actions = {
	async retrieveGlobalRevenueIndicators({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'global_evolution' : 'global'
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/revenue/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveGlobalParticipationRate({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'global_evolution' : 'global'
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats-participation/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveStatGlobal({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsGlobal(state, statKey, companyId, params)) {
			return state.statsGlobal[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsGlobal', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheGlobal', { statKey })
				throw err
			})
	},

	async retrieveRevenueByDate({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/revenue-by-date/`,
			statKey: 'revenue_repartition',
			companyId,
			params,
		})
	},

	async retrievePointsIndicators({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'points_evolution' : 'points'
		return await dispatch('retrieveStatPoints', {
			endpoint: `api/company/${companyId}/point-transactions/stats-points/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrievePointsByDate({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatPoints', {
			endpoint: `api/company/${companyId}/point-transactions/stats-by-date-type/`,
			statKey: 'points_repartition',
			companyId,
			params,
		})
	},

	async retrieveStatPoints({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsPoints(state, statKey, companyId, params)) {
			return state.statsPoints[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsPoints', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCachePoints', { statKey })
				throw err
			})
	},

	async retrieveRewardIndicators({ dispatch }, { companyId, params, evolution }) {
		return await dispatch('retrieveStatsRewards', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/claim-usage/`,
			statName: 'reward',
			companyId,
			params,
			evolution,
		})
	},

	async retrieveRewardUsage({ dispatch }, { companyId, params }) {
		// reward_claimed = récompenses obtenues
		const [rewardClaimed, claimUsage] = await Promise.all([
			dispatch('retrieveStatsRewards', {
				endpoint: `api/company/${companyId}/claims/stats/usage-by-date/`,
				statName: 'reward_claimed',
				companyId,
				params,
			}),
			dispatch('retrieveStatsRewards', {
				endpoint: `api/company/${companyId}/loyalty/orders/stats/claim-usage-by-date/`,
				statName: 'claim_usage',
				companyId,
				params,
			}),
		])

		return [...rewardClaimed, ...claimUsage]
	},

	async retrieveStatsRewards(
		{ state, commit },
		{ endpoint, statName, companyId, params, evolution },
	) {
		const statKey = evolution ? statName + '_evolution' : statName
		if (isInCacheStatsRewards(state, statKey, companyId, params)) {
			return state.statsRewards[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params, statKey })
			.then((data) => {
				commit('setStatsRewards', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheReward', { statKey })
				throw err
			})
	},

	async retrieveRewardsLeaderboard({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatsRewards', {
			endpoint: `api/company/${companyId}/claims/stats/leaderboard`,
			statName: 'leaderboard',
			companyId,
			params,
		})
	},

	async retrieveRepartitionByLevel({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'level_repartition_evolution' : 'level_repartition'
		return await dispatch('retrieveStatVip', {
			endpoint: `api/company/${companyId}/vip-tiers/levels/stats/distribution/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveRewardsLeaderboardNext({ state, commit }) {
		const nextEndpoint = state.statsRewards.leaderboard.next

		if (!nextEndpoint) return

		return await useNuxtApp()
			.$api(nextEndpoint)
			.then((data) => {
				commit('setStatsRewardsNext', { data })
				return data
			})
			.catch((err) => {
				commit('clearCacheReward', { statKey: 'leaderboard' })
				throw err
			})
	},

	async retrieveIncomeByLevel({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatVip', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/revenue-by-level/`,
			statKey: 'revenue_level_date',
			companyId,
			params,
		})
	},

	async retrieveVipLeaderboard({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatVip', {
			endpoint: `api/company/${companyId}/claims/stats/leaderboard-vip/`,
			statKey: 'vip_leaderboard',
			companyId,
			params,
		})
	},

	async retrieveStatVip({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsVip(state, statKey, companyId, params)) {
			return state.statsVip[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsVip', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheVip', { statKey })
				throw err
			})
	},
}

export const mutations = {
	setStatsGlobal(state, { statKey, data, companyId, params }) {
		state.statsGlobal = {
			...state.statsGlobal,
			[statKey]: { ...state.statsGlobal[statKey], ...data },
		}
		state.ttl_statsGlobal[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
		state.cache_key_statsGlobal[statKey] = cacheKey
	},

	setStatsRewards(state, { statKey, data, companyId, params }) {
		state.statsRewards = { ...state.statsRewards, [statKey]: data }
		state.ttl_statsRewards[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsRewards(statKey, companyId, params)
		state.cache_key_statsRewards[statKey] = cacheKey
	},

	setStatsRewardsNext(state, { data }) {
		const { leaderboard } = state.statsRewards
		leaderboard.next = data.next
		leaderboard.results = [...leaderboard.results, ...data.results]
		state.ttl_statsRewards.leaderboard = Date.now() + state.cache_duration
	},

	setStatsPoints(state, { statKey, data, companyId, params }) {
		state.statsPoints = { ...state.statsPoints, [statKey]: data }
		state.ttl_statsPoints[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsPoints(statKey, companyId, params)
		state.cache_key_statsPoints[statKey] = cacheKey
	},

	setStatsVip(state, { statKey, data, companyId, params }) {
		state.statsVip = { ...state.statsVip, [statKey]: data }
		state.ttl_statsVip[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsVip(statKey, companyId, params)
		state.cache_key_statsVip[statKey] = cacheKey
	},

	clearCacheGlobal(state, { statKey }) {
		state.statsGlobal[statKey] = null
		state.ttl_statsGlobal[statKey] = null
		state.cache_key_statsGlobal[statKey] = null
	},

	clearCacheReward(state, { statKey }) {
		state.statsRewards[statKey] = null
		state.ttl_statsRewards[statKey] = null
		state.cache_key_statsRewards[statKey] = null
	},

	clearCachePoints(state, { statKey }) {
		state.statsPoints[statKey] = null
		state.ttl_statsPoints[statKey] = null
		state.cache_key_statsPoints[statKey] = null
	},

	clearCacheVip(state, { statKey }) {
		state.statsVip[statKey] = null
		state.ttl_statsVip[statKey] = null
		state.cache_key_statsVip[statKey] = null
	},

	invalidateStatsRewards(state) {
		state.statsRewards = {}
		state.ttl_statsRewards = {}
		state.cache_key_statsRewards = {}
	},
}
